import { useEffect, useMemo, useState } from 'react'
import { Loader2, Pencil } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { ComplianceTarget, CompliancePeriod } from '@/types'

/**
 * Describes an inherited target the form should surface as a read-only hint
 * when the caller's own target is unset. `label` is how the inheritance
 * source is named in the UI (e.g. "Building default", "Organization default").
 */
export interface InheritedTargetHint {
  label: string
  target: ComplianceTarget
}

export interface ComplianceTargetFormProps {
  /**
   * The target stored on the entity the form edits, or null if unset.
   * Used to seed the form state on mount and on each value change.
   */
  value: ComplianceTarget | null
  /**
   * Optional inherited default to show as a hint when value is null (and
   * as the "fallback in effect" label even when the user is mid-edit but
   * hasn't enabled the override yet). Omit on the Organization form —
   * there is no parent to inherit from.
   */
  inheritedFrom?: InheritedTargetHint | null
  /**
   * When false, the form renders in read-only mode: no Edit / Save
   * buttons, inputs invisible. Use this to gate admin-only mutation UI
   * without hiding the useful read-only summary for viewers.
   */
  canEdit: boolean
  /**
   * Invoked on Save. Resolve the returned promise to exit edit mode.
   * Rejection leaves the form in edit mode so the user can correct
   * their input; the caller is responsible for surfacing the error.
   *
   * - `ComplianceTarget` → set / replace the stored target
   * - `null`            → clear the stored target (inherit upstream)
   *
   * "Don't touch the stored target" is expressed by the user not
   * clicking Save; this callback is only invoked when the user
   * explicitly commits a change.
   */
  onSave: (target: ComplianceTarget | null) => Promise<void>
  /**
   * Label for the "set a target here" checkbox / toggle. Defaults to
   * "Set a compliance target". Pages may override (e.g. "Override
   * inherited target" on Building / Room detail).
   */
  toggleLabel?: string
}

interface FormErrors {
  requiredCleans?: string
}

/**
 * Format a target for read-only display (e.g. "2 cleans / day").
 */
function formatTarget(t: ComplianceTarget): string {
  const unit = t.required_cleans === 1 ? 'clean' : 'cleans'
  return `${t.required_cleans} ${unit} / ${t.period}`
}

/**
 * Shared editor for a ComplianceTarget. Renders a three-state form
 * (unset / set / cleared) that maps to the backend's PATCH wire
 * contract:
 *
 *   - user doesn't hit Save → parent omits the field (unset)
 *   - onSave(target)        → set to target
 *   - onSave(null)          → explicit null, clears the stored target
 *
 * The component is used on the Org / Building / Room edit surfaces.
 * Its inheritance-hint UI is cosmetic — the caller resolves which
 * upstream default to pass in via `inheritedFrom`.
 */
export function ComplianceTargetForm({
  value,
  inheritedFrom,
  canEdit,
  onSave,
  toggleLabel = 'Set a compliance target',
}: ComplianceTargetFormProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [override, setOverride] = useState<boolean>(value !== null)
  const [requiredCleans, setRequiredCleans] = useState<string>(
    value ? String(value.required_cleans) : '1'
  )
  const [period, setPeriod] = useState<CompliancePeriod>(value?.period ?? 'day')
  const [errors, setErrors] = useState<FormErrors>({})
  const [submitting, setSubmitting] = useState(false)

  // Reseed form state whenever the caller's value prop changes (e.g. after
  // a successful save causes the parent to invalidate the query and pass
  // down the fresh server value). Only runs when we're NOT actively
  // editing to avoid stomping on in-flight edits — the form exits edit
  // mode on save success, so by then isEditing is already false.
  useEffect(() => {
    if (isEditing) return
    setOverride(value !== null)
    setRequiredCleans(value ? String(value.required_cleans) : '1')
    setPeriod(value?.period ?? 'day')
    setErrors({})
  }, [value, isEditing])

  const hintText = useMemo(() => {
    if (value) return formatTarget(value)
    if (inheritedFrom) {
      return `Inheriting from ${inheritedFrom.label}: ${formatTarget(inheritedFrom.target)}`
    }
    return 'No target set'
  }, [value, inheritedFrom])

  function validate(): FormErrors {
    const errs: FormErrors = {}
    if (!override) return errs
    // Trim so "  2  " still validates; parseInt ignores leading whitespace
    // but Number('') returns 0, which we want to reject.
    const raw = requiredCleans.trim()
    if (raw === '') {
      errs.requiredCleans = 'Required cleans is required.'
      return errs
    }
    // Reject decimals explicitly — <input type=number> allows them.
    if (!/^\d+$/.test(raw)) {
      errs.requiredCleans = 'Required cleans must be a whole number.'
      return errs
    }
    const n = parseInt(raw, 10)
    if (!Number.isFinite(n) || n < 1) {
      errs.requiredCleans = 'Required cleans must be at least 1.'
      return errs
    }
    return errs
  }

  function handleCancel() {
    // Restore baseline: mirror the prop again so any pending edits vanish.
    setOverride(value !== null)
    setRequiredCleans(value ? String(value.required_cleans) : '1')
    setPeriod(value?.period ?? 'day')
    setErrors({})
    setIsEditing(false)
  }

  async function handleSave() {
    const errs = validate()
    setErrors(errs)
    if (Object.keys(errs).length > 0) return

    const payload: ComplianceTarget | null = override
      ? { required_cleans: parseInt(requiredCleans, 10), period }
      : null

    setSubmitting(true)
    try {
      await onSave(payload)
      setIsEditing(false)
    } catch {
      // Parent surfaces the error via toast; stay in edit mode so the
      // user can retry or cancel. Errors are intentionally not echoed
      // in this component — it is not wired to the toast system and
      // duplicating the message would clutter the UI.
    } finally {
      setSubmitting(false)
    }
  }

  // Read-only: either the user can't edit, or the user hasn't entered
  // edit mode. Either way we just render the hint + (maybe) an Edit button.
  if (!isEditing) {
    return (
      <div
        className="flex flex-wrap items-center justify-between gap-3"
        data-testid="compliance-target-summary"
      >
        <p className="text-sm text-muted-foreground">{hintText}</p>
        {canEdit && (
          <Button type="button" variant="outline" size="sm" onClick={() => setIsEditing(true)}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4" data-testid="compliance-target-form">
      <label className="flex items-center gap-2 text-sm font-medium">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-input"
          checked={override}
          onChange={(e) => setOverride(e.target.checked)}
          aria-label={toggleLabel}
        />
        {toggleLabel}
      </label>

      {!override && inheritedFrom && (
        <p className="text-xs text-muted-foreground">
          Will inherit from {inheritedFrom.label}: {formatTarget(inheritedFrom.target)}
        </p>
      )}

      {override && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="compliance-required-cleans">Required cleans</Label>
            <Input
              id="compliance-required-cleans"
              type="number"
              min={1}
              step={1}
              value={requiredCleans}
              onChange={(e) => setRequiredCleans(e.target.value)}
              aria-invalid={!!errors.requiredCleans || undefined}
              aria-describedby={
                errors.requiredCleans ? 'compliance-required-cleans-error' : undefined
              }
            />
            {errors.requiredCleans && (
              <p id="compliance-required-cleans-error" className="text-xs text-destructive">
                {errors.requiredCleans}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="compliance-period">Period</Label>
            <Select value={period} onValueChange={(v) => setPeriod(v as CompliancePeriod)}>
              <SelectTrigger id="compliance-period" aria-label="Period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Day</SelectItem>
                <SelectItem value="week">Week</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button type="button" onClick={handleSave} disabled={submitting}>
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving…
            </>
          ) : (
            'Save'
          )}
        </Button>
        <Button type="button" variant="outline" onClick={handleCancel} disabled={submitting}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
